import React, { useCallback, useEffect, useMemo, useState } from "react";
/**
 * Items Provider
 *
 * Provides category and product functionality to the Items feature.
 * Manages both CategoryContext and ProductContext.
 * Integrates with CategoryService and ProductService for business logic.
 * Replaces the old AppContext for Items-related functionality.
 */

import { useQueryClient } from "@tanstack/react-query";
import {
  useExternalCategoriesControllerGetCategoryTree,
  getExternalCategoriesControllerGetCategoryTreeQueryKey,
  getExternalItemsControllerGetItemsQueryKey,
  useExternalSuppliersControllerUpdateSupplierMe,
} from "@semo/api-contracts";
import {
  CategoryContext,
  CategoryContextType,
} from "../../application/contexts/CategoryContext";
import {
  ProductContext,
  ProductContextType,
} from "../../application/contexts/ProductContext";
import {
  OrderContext,
  OrderContextType,
} from "../../application/contexts/OrderContext";
import type { DeliverySettings, Product } from "../../../types";
import { Category } from "../../domain/entities/Category";
import { CategoryService } from "../../application/services/CategoryService";
import { getCategoryService } from "../../infrastructure/services/ItemsServiceFactory";
import {
  CategoryMapper,
  type CategoryDTO,
} from "../../infrastructure/mappers/Category.mapper";

interface ItemsProviderProps {
  children: React.ReactNode;
}

export const ItemsProvider: React.FC<ItemsProviderProps> = ({ children }) => {
  const queryClient = useQueryClient();

  // ========== SERVICE INITIALIZATION ==========
  const categoryService = useMemo(() => getCategoryService(), []);

  // ========== CATEGORY STATE & OPERATIONS ==========
  const {
    data: apiResponse,
    isLoading: categoriesLoading,
    error: categoriesError,
  } = useExternalCategoriesControllerGetCategoryTree(undefined);

  const [categories, setCategories] = useState<Category[]>([]);

  // Load initial categories from service
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const cats = await categoryService.getAllCategories();
        setCategories(cats);
      } catch (error) {
        console.error("Failed to load categories:", error);
      }
    };

    loadCategories();
  }, [categoryService]);

  // ✅ Type-safe: sync with API response using CategoryMapper
  useEffect(() => {
    const categoriesTree = (apiResponse?.data || []) as CategoryDTO[];

    if (Array.isArray(categoriesTree) && categoriesTree.length > 0) {
      // ✅ Use CategoryMapper for type-safe tree flattening
      const flattenedCategories = CategoryMapper.flattenTree(categoriesTree);
      setCategories(flattenedCategories);
    }
  }, [apiResponse]);

  const addCategory = useCallback(
    async (
      name: string,
      parentId: string | null,
      level: number,
    ): Promise<void> => {
      try {
        const newCategory = await categoryService.createCategory(
          name,
          parentId,
          level,
        );
        setCategories((prev) => [...prev, newCategory]);
        await queryClient.invalidateQueries({
          queryKey: getExternalCategoriesControllerGetCategoryTreeQueryKey(),
        });
      } catch (error) {
        console.error("Failed to add category:", error);
        throw error;
      }
    },
    [categoryService, queryClient],
  );

  const addCategories = useCallback(
    async (
      newCategories: {
        id: string;
        name: string;
        parentId: string | null;
        level: number;
      }[],
    ): Promise<void> => {
      try {
        // Convert plain objects to Category domain entities for state management
        const categoriesAsEntities = newCategories.map((cat) => {
          const category = new Category({
            id: cat.id,
            name: cat.name,
            parentId: cat.parentId,
            level: cat.level,
          });
          return category;
        });
        setCategories((prev) => [...prev, ...categoriesAsEntities]);
        await queryClient.invalidateQueries({
          queryKey: getExternalCategoriesControllerGetCategoryTreeQueryKey(),
        });
      } catch (error) {
        console.error("Failed to add categories:", error);
        throw error;
      }
    },
    [queryClient],
  );

  const removeCategory = useCallback(
    async (id: string): Promise<void> => {
      try {
        await categoryService.deleteCategory(id);
        setCategories((prev) => prev.filter((c) => c.id !== id));
        await queryClient.invalidateQueries({
          queryKey: getExternalCategoriesControllerGetCategoryTreeQueryKey(),
        });
      } catch (error) {
        console.error("Failed to remove category:", error);
        throw error;
      }
    },
    [categoryService, queryClient],
  );

  const updateCategory = useCallback(
    async (id: string, name: string): Promise<void> => {
      try {
        const updated = await categoryService.updateCategory(id, name);
        setCategories((prev) => prev.map((c) => (c.id === id ? updated : c)));
        await queryClient.invalidateQueries({
          queryKey: getExternalCategoriesControllerGetCategoryTreeQueryKey(),
        });
      } catch (error) {
        console.error("Failed to update category:", error);
        throw error;
      }
    },
    [categoryService, queryClient],
  );

  const refreshCategories = useCallback(async (): Promise<void> => {
    try {
      const cats = await categoryService.getAllCategories();
      setCategories(cats);
      await queryClient.invalidateQueries({
        queryKey: getExternalCategoriesControllerGetCategoryTreeQueryKey(),
      });
    } catch (error) {
      console.error("Failed to refresh categories:", error);
    }
  }, [categoryService, queryClient]);

  const categoryValue: CategoryContextType = {
    categories,
    isLoading: categoriesLoading,
    error: (categoriesError as Error) || null,
    addCategory,
    addCategories,
    removeCategory,
    updateCategory,
    refreshCategories,
  };

  // ========== PRODUCT STATE & OPERATIONS ==========
  const [products, setProducts] = useState<Product[]>([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsError, setProductsError] = useState<Error | null>(null);

  // ✅ REMOVED: Initial products useEffect (no longer needed - products loaded via Orval hooks)
  // Products will be loaded by components using Orval hooks
  // Cache invalidation will keep them in sync

  const addProduct = useCallback(
    async (product: Omit<Product, "id">): Promise<void> => {
      try {
        // ✅ Cache invalidation: Orval mutation already called in ProductMainPanel.tsx:112
        // This callback only invalidates cache to trigger auto-refresh
        await queryClient.invalidateQueries({
          queryKey: getExternalItemsControllerGetItemsQueryKey(),
        });
        setProductsError(null);
      } catch (error) {
        console.error("Failed to invalidate product cache:", error);
        setProductsError(error as Error);
        throw error;
      }
    },
    [queryClient],
  );

  const updateProduct = useCallback(
    async (id: string, updates: Partial<Product>): Promise<void> => {
      try {
        // ✅ Cache invalidation: Orval mutation already called in ProductMainPanel.tsx
        // This callback only invalidates cache to trigger auto-refresh
        await queryClient.invalidateQueries({
          queryKey: getExternalItemsControllerGetItemsQueryKey(),
        });
        setProductsError(null);
      } catch (error) {
        console.error(
          "Failed to invalidate product cache after update:",
          error,
        );
        setProductsError(error as Error);
        throw error;
      }
    },
    [queryClient],
  );

  const removeProduct = useCallback(
    async (id: string): Promise<void> => {
      try {
        // ✅ Cache invalidation: Orval mutation already called in ProductMainPanel.tsx
        // This callback only invalidates cache to trigger auto-refresh
        await queryClient.invalidateQueries({
          queryKey: getExternalItemsControllerGetItemsQueryKey(),
        });
        setProductsError(null);
      } catch (error) {
        console.error(
          "Failed to invalidate product cache after delete:",
          error,
        );
        setProductsError(error as Error);
        throw error;
      }
    },
    [queryClient],
  );

  const refreshProducts = useCallback(async (): Promise<void> => {
    try {
      // ✅ Cache invalidation: Manually refresh products by invalidating cache
      // This triggers React Query to refetch products from API
      await queryClient.invalidateQueries({
        queryKey: getExternalItemsControllerGetItemsQueryKey(),
      });
      setProductsError(null);
    } catch (error) {
      console.error("Failed to refresh product cache:", error);
      setProductsError(error as Error);
    }
  }, [queryClient]);

  const productValue: ProductContextType = {
    products,
    isLoading: productsLoading,
    error: productsError,
    addProduct,
    updateProduct,
    removeProduct,
    refreshProducts,
    updateSupplierSettings: async (id: string, settings: any) => {
      await queryClient.invalidateQueries({
        queryKey: getExternalItemsControllerGetItemsQueryKey(),
      });
    },
  };

  // ========== ORDER STATE & OPERATIONS ==========
  const [orderItems, setOrderItems] = useState<any[]>([]);

  const addToOrder = useCallback((product: any) => {
    setOrderItems((prev) => [
      ...prev,
      {
        ...product,
        orderPrice: product.price,
        quantity: 1,
      },
    ]);
  }, []);

  const updateOrderItem = useCallback((id: string, updates: any) => {
    setOrderItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, ...updates } : item)),
    );
  }, []);

  const removeFromOrder = useCallback((id: string) => {
    setOrderItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const orderValue: OrderContextType = {
    orderItems,
    addToOrder,
    updateOrderItem,
    removeFromOrder,
  };

  // ========== PROVIDERS ==========
  return (
    <OrderContext.Provider value={orderValue}>
      <CategoryContext.Provider value={categoryValue}>
        <ProductContext.Provider value={productValue}>
          {children}
        </ProductContext.Provider>
      </CategoryContext.Provider>
    </OrderContext.Provider>
  );
};
